"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Calendar, Flame, ShieldCheck, Clock, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function HeaterCta() {
  const shouldReduceMotion = useReducedMotion();
  const features = [{
    icon: Clock,
    title: "24/7 Rapid Response",
    description: "Immediate local dispatch in Jonesboro and surrounding areas."
  }, {
    icon: Flame,
    title: "Gas System Specialists",
    description: "Certified diagnostics for pilot lights, burners, valves, and venting."
  }, {
    icon: ShieldCheck,
    title: "Upfront Honest Pricing",
    description: "Clear repair or replacement quotes with zero surprise fees."
  }];
  return <section id="gaswaterheatercta" data-nav="light" className="relative bg-background py-16 md:py-24 overflow-hidden border-t border-border">
      {/* Decorative subtle background elements */}
      <div className="absolute inset-0 pointer-events-none -z-10 flex items-center justify-center">
        <div className="w-[600px] h-[300px] bg-accent/5 rounded-full blur-3xl" />
        <div className="w-[400px] h-[250px] bg-primary/5 rounded-full blur-3xl -translate-y-12" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="relative rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 lg:p-16 shadow-xl overflow-hidden">
          {/* Subtle grid pattern overlay */}
          <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-accent/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -left-16 -top-16 w-60 h-60 bg-white/5 rounded-full blur-xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Column: Heading & Availability info */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="bg-accent text-accent-foreground border-transparent font-semibold px-3 py-1 text-xs sm:text-sm tracking-wide">
                  <Flame className="size-3.5 mr-1 fill-current" />
                  Gas Water Heater Experts
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-primary-foreground/80 font-medium">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
                  </span>
                  Ready to Dispatch Now
                </span>
              </div>

              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-white tracking-tight leading-tight">
                  No Hot Water or Smelling Gas? Get Fast Help Today.
                </h2>
                <p className="text-primary-foreground/85 text-base sm:text-lg max-w-xl leading-relaxed">
                  Our licensed plumbers handle gas water heater leaks, burner failures, and emergency replacements with same-day priority service across Jonesboro, GA.
                </p>
              </div>

              {/* Service guarantee pills */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                {features.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="flex flex-col gap-1 rounded-lg bg-white/10 p-3.5 backdrop-blur-xs border border-white/10">
                      <div className="flex items-center gap-2">
                        <Icon className="size-4 text-accent shrink-0" />
                        <span className="font-semibold text-xs sm:text-sm text-white">
                          {item.title}
                        </span>
                      </div>
                      <p className="text-xs text-primary-foreground/70 line-clamp-2">
                        {item.description}
                      </p>
                    </div>;
              })}
              </div>
            </motion.div>

            {/* Right Column: Direct Call Action & Online Booking */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }} className="lg:col-span-5">
              <div className="rounded-xl bg-card text-card-foreground p-6 sm:p-8 shadow-2xl border border-border flex flex-col items-center text-center space-y-6">
                <div className="space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Emergency Hotline & Dispatch
                  </span>
                  <p className="text-sm text-card-foreground/80">
                    Speak directly with an on-call water heater specialist
                  </p>
                </div>

                {/* Prominent Gold Phone Banner */}
                <a href="tel:4708479076" className="w-full group relative flex flex-col items-center justify-center gap-1.5 rounded-xl bg-accent px-6 py-4 text-accent-foreground shadow-md transition-[transform,box-shadow] duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center gap-2 font-bold text-2xl sm:text-3xl tracking-tight">
                    <PhoneCall className="size-6 shrink-0 transition-transform group-hover:rotate-12 duration-200" />
                    <span>(470) 847-9076</span>
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent-foreground/80">
                    Tap to Call 24/7 Available
                  </span>
                </a>

                <div className="w-full flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs uppercase font-medium text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {/* Request Service Button */}
                <div className="w-full">
                  <Button asChild size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 shadow-xs group">
                    <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                      <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                      <span>Request Service Online</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 duration-200" />
                    </Link>
                  </Button>
                </div>

                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <ShieldCheck className="size-3.5 text-accent" />
                    Licensed & Insured
                  </span>
                  <span>•</span>
                  <span>Jonesboro, GA</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}